/// <reference path="../../../typings/browser.d.ts" />
/// <reference path="../../../customtypings/parsley/parsley.d.ts" />

namespace Application.Controllers {
    'use strict';

    export interface IHealthChecksModalScope extends ng.IScope {
        networkElement: Models.ITopologyElement;
        newTcpPing: Models.ITCPPing;
        showform: boolean;
        modalClose: () => void;
        addTcpPing: () => void;
        editTcpPing: () => void;
        removeCheck:  (checkId: string) => void;
        startCheckEdition: (check: Models.INodeCheck) => void;
        toId: (href: string) => number;
        formatDate: (date: string) => string;
    }

    export class HealthChecksModalController {
        public static templateURL: string = "decorators/modals/health-checks-modal.html";
        public static ID: string = "#id-health-checks-modal";
        public static $name: string = "healthChecksModalController";
        public static $inject: string[] = ['$scope',
                                           'close',
                                           '$interval',
                                           '$timeout',
                                           'networkElement',
                                           'timezoneService',
                                           'healthService'];
        private static PARSLEY_CHECK_VALIDATE: string = 'validateName';
        private static DEFAULT_DATE: string = new Date().toString();

        private checkInterval: ng.IPromise<{}>;

        /**
         * Constructor
         */
        constructor(private $scope: IHealthChecksModalScope,
                    private close: (parameters: ngModal.ICloseParameters) => void,
                    private $interval: ng.IIntervalService,
                    private $timeout: ng.ITimeoutService,
                    private networkElement: Models.ITopologyElement,
                    private timezoneService: Services.TimezoneService,
                    private healthService: Services.HealthService) {
            this.$scope.networkElement = networkElement;
            this.$scope.showform = false;
            this.addParsleyValidateName();

            $timeout(() => {
                window.altair_admin_common_init();
            }, 500);

            this.$scope.newTcpPing = {
                port: undefined,
                checkName: "",
                networkElementHref: networkElement.href,
            };
            this.close = close;

            this.$scope.modalClose = (): void => {
                this.close(undefined);
            };

            this.$scope.addTcpPing = (): void => {
                if (this.validateBeforeSubmit($scope.newTcpPing)) {
                    if (this.$scope.newTcpPing.checkId === 'new') {
                        this.$scope.newTcpPing.port = parseInt(this.$scope.newTcpPing.port as string, 10);
                        this.healthService
                            .addTcpPingCheckForElement(networkElement, this.$scope.newTcpPing)
                            .then((value: {}) => {
                                this.$scope.showform = false;
                                this.$scope.networkElement.health.data.checks.push({
                                    name: this.$scope.newTcpPing.checkName,
                                    type: "TCP",
                                    healthStatus: "PENDING",
                                    notes: "Pending",
                                    output: "Pending...",
                                    checkId: "new",
                                });
                                this.$scope.newTcpPing = this.emptyTcpPing();
                            })
                            .catch((reason: ngCustom.IReason) => {
                                Common.Utils.alertModal(reason.data.message, "danger");
                            });
                    } else {
                        this.healthService
                            .editTcpPingCheckForElement(networkElement, this.$scope.newTcpPing, this.$scope.newTcpPing.checkId)
                            .then((value: {}) => {
                                this.$scope.showform = false;
                                this.$scope.newTcpPing = this.emptyTcpPing();
                            })
                            .catch((reason: ngCustom.IReason) => {
                                Common.Utils.alertModal(reason.data.message, "danger");
                            });
                    }
                }
            };

            this.$scope.startCheckEdition = (check: Models.INodeCheck): void => {
                if (check === undefined) {
                    this.$scope.newTcpPing = this.emptyTcpPing();
                } else {
                    this.$scope.newTcpPing = {
                        port: this.parsePort(check.notes),
                        checkName: check.name,
                        networkElementHref: networkElement.href,
                        checkId: check.checkId,
                    };
                }
                $timeout(() => {
                    this.$scope.showform = true;
                    window.altair_forms_validation();
                });
            };

            this.$scope.removeCheck = (checkId: string): void => {
                this.healthService.removeTcpPing(networkElement, checkId).then( (value: {}) => {
                        this.removeCheckFromCurrentView(checkId);
                });
            };

            this.$scope.toId = (href: string): number => {
                return Common.Utils.idFromHref(href);
            };

            this.$scope.formatDate = (date: string): string => {
                let offset: string = this.timezoneService.getEffectiveOffset();
                let dateOffseted: string;
                if (!date) {
                    date = HealthChecksModalController.DEFAULT_DATE;
                }
                dateOffseted = date.slice(0, -5) + offset;
                let dateObject: Date = new Date(dateOffseted);
                let formatter: moment.Moment = moment( dateObject );
                return formatter.format('YYYY-MM-DD HH:mm:ss');
            };

            this.$scope.$on('$destroy', () => {
                window.Parsley.removeValidator(HealthChecksModalController.PARSLEY_CHECK_VALIDATE);
                if (this.checkInterval) {
                    this.$interval.cancel(this.checkInterval);
                }
            });

            this.healthService.refreshNodeHealth(networkElement.href);
        }

        private addParsleyValidateName(): void {
            window.Parsley
            .addValidator(HealthChecksModalController.PARSLEY_CHECK_VALIDATE, {
                validateString: (checkName: string, checkId?: number): boolean => {
                    return this.validateName(checkName, checkId.toString());
                },
                messages: {
                    en: 'The name already exists',
                },
            });
        }

        private validateBeforeSubmit(tcpPing: Models.ITCPPing): boolean {
            let isNameValid: boolean = this.validateName(tcpPing.checkName, tcpPing.checkId);

            return tcpPing
                && tcpPing.checkName !== undefined && tcpPing.checkName.length > 0 && isNameValid
                && tcpPing.port !== undefined && !isNaN(parseInt(tcpPing.port as string, 10))
                && tcpPing.networkElementHref !== undefined && tcpPing.networkElementHref.length > 0;
        }

        private validateName(checkName: string, checkId?: string): boolean {
            let isNameValid: boolean = true;
            for (let i: number = 0; i < this.$scope.networkElement.health.data.checks.length ; i++) {
                if (this.$scope.networkElement.health.data.checks[i].name === checkName &&
                    (checkId === undefined || this.$scope.networkElement.health.data.checks[i].checkId !== checkId))
                    isNameValid = false;
            }
            return isNameValid;
        }

        private parsePort(notes: string): number {
            let ixGw: number = notes.indexOf('=> ');
            if (ixGw >= 0) {
                return parseInt(notes.substring(ixGw + 3), 10);
            } else {
                let ix: number = notes.indexOf('port ');
                if (ix >= 0)
                    return parseInt(notes.substring(ix + 5), 10);
                else
                    return undefined;
            }
        }

        private removeCheckFromCurrentView(checkId: string): void {
            let checks: Models.INodeCheck[] = this.$scope.networkElement.health.data.checks;
            let indexToRemove: number = -1;
            for (let index: number = 0; index < checks.length; index++) {
                let check: Models.INodeCheck = checks[index];
                if (check.checkId === checkId) {
                    indexToRemove = index;
                }
            }
            if (indexToRemove >= 0) {
                checks.splice(indexToRemove, 1);
            }
        }

        private emptyTcpPing(): Models.ITCPPing {
            return {
                port: undefined,
                checkName: "",
                networkElementHref: this.$scope.networkElement.href,
                checkId: "new",
            };
        }
    }
}
